import React, { useState } from 'react';
import { Container } from '@mui/material';
import {
    AppBar, Toolbar, Typography, Button, Box, IconButton, Menu, MenuItem,
    Avatar, Chip, useTheme, alpha, Stack
} from '@mui/material';
import {
    Menu as MenuIcon, AccountCircle, Logout, Person, EmojiEvents,
    Home, Category, Info, ContactMail, School, HowToVote, Dashboard,
    Settings, Assessment, Security, Lightbulb, Feedback
} from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import useScrollDirection from '../../hooks/useScrollDirection';

const Header = () => {
    const {user, isAuthenticated, logout} = useAuth();
    const theme = useTheme();
    const navigate = useNavigate();
    const [anchorEl, setAnchorEl] = useState(null);
    const [mobileMenuAnchorEl, setMobileMenuAnchorEl] = useState(null);

    // Add scroll direction detection
    const { scrollDirection, isAtTop } = useScrollDirection();

    const handleMenu = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMobileMenu = (event) => {
        setMobileMenuAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
        setMobileMenuAnchorEl(null);
    };

    const handleLogout = () => {
        logout();
        navigate('/');
        handleClose();
    };

    // Check if user is admin
    const isAdmin = user && (user.role === 'ADMIN' || user.role === 'SUPER_ADMIN');

    // Navigation items configuration
    const navConfig = {
        public: [
            {label: 'Home', path: '/', icon: <Home/>},
            {label: 'Classify', path: '/classify', icon: <School/>},
            {label: 'Tips', path: '/tips', icon: <Lightbulb/>},
            {label: 'Features', path: '/features', icon: <Category/>},
            {label: 'Leaderboard', path: '/leaderboard', icon: <EmojiEvents/>},
        ],
        authenticated: [
            {label: 'Classify', path: '/classify', icon: <School/>},
            ...(isAdmin ? [] : [{label: 'Tips', path: '/tips', icon: <Lightbulb/>}]), // Hide Tips for admins
            {label: 'History', path: '/history', icon: <Category/>},
            {label: 'Rewards', path: '/rewards', icon: <EmojiEvents/>},
            {label: 'Leaderboard', path: '/leaderboard', icon: <EmojiEvents/>},
            ...(isAdmin ? [{label: 'Dashboard', path: '/admin/dashboard', icon: <Dashboard/>}] : [])
        ],
        admin: [
            {label: 'Dashboard', path: '/admin/dashboard', icon: <Dashboard/>},
            {label: 'Badge Management', path: '/admin/badges', icon: <EmojiEvents/>},
            {label: 'User Management', path: '/admin/users', icon: <Person/>},
            {label: 'Category Management', path: '/admin/categories', icon: <Category/>},
            {label: 'Tips Management', path: '/admin/tips', icon: <Lightbulb/>},
            {label: 'Feedback Management', path: '/admin/feedback', icon: <Feedback/>},
            {label: 'Statistics & Analytics', path: '/admin/stats', icon: <Assessment/>},
            {label: 'System Logs', path: '/admin/logs', icon: <Security/>}
        ]
    };

    const displayNavItems = isAuthenticated ? navConfig.authenticated : navConfig.public;

    // Check if header should be visible on mobile
    const shouldShowHeader = isAtTop || scrollDirection === 'up';

    return (
        <AppBar
            position="sticky"
            elevation={4}
            sx={{
                background: 'linear-gradient(135deg, #43a047 0%, #2e7d32 100%)',
                backdropFilter: 'blur(10px)',
                borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.12)}`,
                // Mobile auto-hide behavior
                transform: {
                    xs: shouldShowHeader ? 'translateY(0)' : 'translateY(-100%)',
                    md: 'translateY(0)' // Always visible on desktop
                },
                transition: {
                    xs: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)', // Sharp Nokia-style animation
                    md: 'none'
                },
                // Ensure header stays above other content when hidden
                zIndex: theme.zIndex.appBar
            }}
        >
            <Toolbar disableGutters sx={{py: 1}}>
                <Container maxWidth="lg" sx={{display: 'flex', alignItems: 'center'}}>
                    {/* Logo & Brand (now aligned with hero container) */}
                    <Box
                        component={Link}
                        to="/"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            textDecoration: 'none',
                            '&:hover': {transform: 'scale(1.05)'},
                            transition: 'transform 0.2s ease'
                        }}
                    >
                        <Box sx={{display: 'flex', alignItems: 'center', position: 'relative'}}>
                            <Typography
                                variant="h4"
                                fontWeight={900}
                                sx={{
                                    color: 'white',
                                    textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                                    background: 'linear-gradient(135deg, #ffffff 0%, #e8f5e8 100%)',
                                    backgroundClip: 'text',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    lineHeight: 1,
                                    mb: 0,
                                    pr: 5 // keep gap for the chip
                                }}
                            >
                                🌱 Sortify
                            </Typography>
                            <Chip
                                label="AI-Powered"
                                size="small"
                                sx={{
                                    position: 'absolute',
                                    bottom: -2,
                                    left: 'calc(100% - 38px)', // just after 'y'
                                    background: 'rgba(255,255,255,0.3)',
                                    color: 'white',
                                    fontWeight: 700,
                                    display: {xs: 'none', sm: 'flex'},
                                    fontSize: '0.65rem',
                                    height: 20,
                                    minWidth: 32,
                                    backdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(255,255,255,0.4)',
                                    transform: 'scale(1.0)',
                                    '& .MuiChip-label': {px: 1}
                                }}
                            />
                        </Box>
                    </Box>

                    {/* Spacer pushes nav & actions to the right, keeps logo aligned with hero */}
                    <Box sx={{flexGrow: 1}}/>

                    {/* Desktop Navigation (unchanged) */}
                    <Box sx={{display: {xs: 'none', md: 'flex'}, alignItems: 'center', gap: 1}}>
                        {displayNavItems.map((item) => (
                            <Button
                                key={item.path}
                                component={Link}
                                to={item.path}
                                startIcon={item.icon}
                                sx={{
                                    color: 'white',
                                    fontWeight: 600,
                                    px: 2,
                                    py: 1,
                                    borderRadius: 3,
                                    textTransform: 'none',
                                    '&:hover': {
                                        background: alpha(theme.palette.common.white, 0.1),
                                        transform: 'translateY(-1px)'
                                    },
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                {item.label}
                            </Button>
                        ))}
                    </Box>

                    {/* User Actions (unchanged) */}
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
                        {isAuthenticated ? (
                            <>
                                {/* User Avatar & Menu */}
                                <Stack direction="row" alignItems="center" spacing={2}>
                                    {user?.rewardsPoints > 0 && (
                                        <Chip
                                            icon={<EmojiEvents sx={{fontSize: '18px !important'}}/>}
                                            label={`${user.rewardsPoints} pts`}
                                            size="small"
                                            sx={{
                                                background: 'rgba(255,255,255,0.2)',
                                                color: 'white',
                                                fontWeight: 700,
                                                backdropFilter: 'blur(10px)',
                                                border: '1px solid rgba(255,255,255,0.3)',
                                                '& .MuiChip-icon': {color: 'white'},
                                                display: { xs: 'none', sm: 'flex' } // Hide points on mobile
                                            }}
                                        />
                                    )}

                                    <IconButton
                                        size="large"
                                        aria-label="account menu"
                                        aria-controls="profile-menu"
                                        aria-haspopup="true"
                                        onClick={handleMenu}
                                        sx={{
                                            color: 'white',
                                            '&:hover': {
                                                background: alpha(theme.palette.common.white, 0.1),
                                                transform: 'scale(1.1)'
                                            }
                                        }}
                                    >
                                        <Avatar
                                            src={user?.profilePhotoPath}
                                            sx={{
                                                width: 40,
                                                height: 40,
                                                border: '2px solid white',
                                                boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                                            }}
                                        >
                                            {user?.displayName?.charAt(0) || <Person/>}
                                        </Avatar>
                                    </IconButton>
                                </Stack>

                                {/* User Profile Menu */}
                                <Menu
                                    id="profile-menu"
                                    anchorEl={anchorEl}
                                    anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                                    transformOrigin={{vertical: 'top', horizontal: 'right'}}
                                    open={Boolean(anchorEl)}
                                    onClose={handleClose}
                                    PaperProps={{
                                        sx: {
                                            mt: 1,
                                            borderRadius: 3,
                                            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                                            minWidth: 200
                                        }
                                    }}
                                >
                                    <Box sx={{px: 2, py: 1.5, borderBottom: '1px solid #f0f0f0'}}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Signed in as
                                        </Typography>
                                        <Typography variant="h6" fontWeight={600}>
                                            {user?.displayName || 'User'}
                                        </Typography>
                                        {user?.role && (user.role === 'ADMIN' || user.role === 'SUPER_ADMIN') && (
                                            <Chip
                                                label={user.role}
                                                size="small"
                                                sx={{
                                                    background: 'linear-gradient(135deg, #1976d2, #42a5f5)',
                                                    color: 'white',
                                                    fontWeight: 600,
                                                    fontSize: '0.7rem',
                                                    height: 20,
                                                    mt: 0.5
                                                }}
                                            />
                                        )}
                                    </Box>

                                    <MenuItem component={Link} to="/profile" onClick={handleClose}>
                                        <Person sx={{mr: 1}}/> Profile
                                    </MenuItem>
                                    <MenuItem component={Link} to="/rewards" onClick={handleClose}>
                                        <EmojiEvents sx={{mr: 1}}/> Rewards
                                    </MenuItem>

                                    {/* Admin Navigation Menu Items */}
                                    {isAdmin && (
                                        <>
                                            <Box sx={{borderTop: '1px solid #f0f0f0', mt: 1, pt: 1}}>
                                                <Typography variant="caption" sx={{
                                                    px: 2,
                                                    py: 1,
                                                    color: 'text.secondary',
                                                    fontWeight: 600
                                                }}>
                                                    🛠️ Admin Panel
                                                </Typography>
                                            </Box>

                                            {navConfig.admin.map((item) => (
                                                <MenuItem
                                                    key={item.path}
                                                    component={Link}
                                                    to={item.path}
                                                    onClick={handleClose}
                                                    disabled={item.comingSoon}
                                                    sx={{
                                                        color: item.comingSoon ? 'text.disabled' : '#1976d2',
                                                        '&:hover': {
                                                            background: item.comingSoon ? 'none' : 'rgba(25, 118, 210, 0.08)'
                                                        }
                                                    }}
                                                >
                                                    {React.cloneElement(item.icon, {sx: {mr: 1}})}
                                                    {item.label}
                                                    {item.comingSoon && (
                                                        <Chip
                                                            label="Soon"
                                                            size="small"
                                                            sx={{
                                                                ml: 1,
                                                                height: 16,
                                                                fontSize: '0.6rem',
                                                                background: '#ff9800',
                                                                color: 'white'
                                                            }}
                                                        />
                                                    )}
                                                </MenuItem>
                                            ))}
                                        </>
                                    )}

                                    <MenuItem onClick={handleLogout}
                                              sx={{color: 'error.main', borderTop: '1px solid #f0f0f0', mt: 1, pt: 1}}>
                                        <Logout sx={{mr: 1}}/> Logout
                                    </MenuItem>
                                </Menu>
                            </>
                        ) : (
                            /* Clean Auth Buttons - Hidden on mobile, shown in mobile menu */
                            <Stack direction="row" spacing={1} sx={{ display: { xs: 'none', sm: 'flex' } }}>
                                <Button
                                    component={Link}
                                    to="/login"
                                    variant="outlined"
                                    sx={{
                                        color: 'white',
                                        borderColor: 'white',
                                        fontWeight: 600,
                                        px: 3,
                                        borderRadius: 3,
                                        '&:hover': {
                                            borderColor: 'white',
                                            background: alpha(theme.palette.common.white, 0.1)
                                        }
                                    }}
                                >
                                    Login
                                </Button>
                                <Button
                                    component={Link}
                                    to="/register"
                                    variant="contained"
                                    sx={{
                                        background: 'linear-gradient(135deg, #ffd600 0%, #ffc400 100%)',
                                        color: '#212121',
                                        fontWeight: 700,
                                        px: 3,
                                        borderRadius: 3,
                                        boxShadow: '0 4px 14px 0 rgba(255, 196, 0, 0.3)',
                                        '&:hover': {
                                            background: 'linear-gradient(135deg, #ffea00 0%, #ffd600 100%)',
                                            transform: 'translateY(-1px)',
                                            boxShadow: '0 6px 20px 0 rgba(255, 196, 0, 0.4)'
                                        }
                                    }}
                                >
                                    Join Now
                                </Button>
                            </Stack>
                        )}

                        {/* Enhanced Mobile Menu Button - Always visible with better contrast */}
                        <IconButton
                            size="large"
                            aria-label="mobile menu"
                            aria-controls="mobile-menu"
                            aria-haspopup="true"
                            onClick={handleMobileMenu}
                            sx={{
                                display: { xs: 'flex', md: 'none' },
                                color: 'white',
                                bgcolor: alpha(theme.palette.common.white, 0.1),
                                border: '2px solid rgba(255,255,255,0.3)',
                                backdropFilter: 'blur(10px)',
                                '&:hover': {
                                    bgcolor: alpha(theme.palette.common.white, 0.2),
                                    transform: 'scale(1.05)',
                                    borderColor: 'rgba(255,255,255,0.5)'
                                },
                                '& .MuiSvgIcon-root': {
                                    fontSize: '1.8rem'
                                }
                            }}
                        >
                            <MenuIcon/>
                        </IconButton>

                        {/* Enhanced Mobile Menu */}
                        <Menu
                            id="mobile-menu"
                            anchorEl={mobileMenuAnchorEl}
                            anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                            transformOrigin={{vertical: 'top', horizontal: 'right'}}
                            open={Boolean(mobileMenuAnchorEl)}
                            onClose={handleClose}
                            sx={{display: {xs: 'block', md: 'none'}}}
                            PaperProps={{
                                sx: {
                                    mt: 1,
                                    borderRadius: 4,
                                    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                                    minWidth: 220,
                                    maxWidth: 280
                                }
                            }}
                        >
                            {/* Mobile Navigation Items */}
                            {displayNavItems.map((item) => (
                                <MenuItem
                                    key={item.path}
                                    component={Link}
                                    to={item.path}
                                    onClick={handleClose}
                                    sx={{
                                        py: 1.5,
                                        px: 2,
                                        '&:hover': {
                                            bgcolor: 'rgba(67, 160, 71, 0.08)'
                                        }
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                        {React.cloneElement(item.icon, {
                                            sx: { mr: 2, color: '#43a047', fontSize: '1.2rem' }
                                        })}
                                        <Typography variant="body1" fontWeight={600}>
                                            {item.label}
                                        </Typography>
                                    </Box>
                                </MenuItem>
                            ))}

                            {/* Mobile Auth Buttons - Only show for non-authenticated users */}
                            {!isAuthenticated && (
                                <>
                                    <Box sx={{ borderTop: '1px solid #f0f0f0', mt: 1, pt: 1 }}>
                                        <MenuItem
                                            component={Link}
                                            to="/login"
                                            onClick={handleClose}
                                            sx={{
                                                py: 1.5,
                                                mx: 1,
                                                borderRadius: 2,
                                                bgcolor: 'rgba(67, 160, 71, 0.1)',
                                                '&:hover': {
                                                    bgcolor: 'rgba(67, 160, 71, 0.2)'
                                                }
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                                <AccountCircle sx={{ mr: 2, color: '#43a047' }} />
                                                <Typography variant="body1" fontWeight={700} color="#43a047">
                                                    🔐 Login
                                                </Typography>
                                            </Box>
                                        </MenuItem>
                                        <MenuItem
                                            component={Link}
                                            to="/register"
                                            onClick={handleClose}
                                            sx={{
                                                py: 1.5,
                                                mx: 1,
                                                mt: 1,
                                                borderRadius: 2,
                                                background: 'linear-gradient(135deg, #ffd600 0%, #ffc400 100%)',
                                                color: '#212121',
                                                '&:hover': {
                                                    background: 'linear-gradient(135deg, #ffea00 0%, #ffd600 100%)',
                                                    transform: 'translateY(-1px)'
                                                }
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                                <Person sx={{ mr: 2, color: '#212121' }} />
                                                <Typography variant="body1" fontWeight={700} color="#212121">
                                                    🚀 Join Free
                                                </Typography>
                                            </Box>
                                        </MenuItem>
                                    </Box>
                                </>
                            )}
                        </Menu>
                    </Box>
                </Container>
            </Toolbar>
        </AppBar>
    );
};
export default Header;
